// 公開 API（netlify/functions/publish.mjs）の純粋ロジック。
// 正本: docs/ゲーム公開実装＆運用計画書.md §5〜§6（検証・クォータ・状態遷移はサーバ側で再検査）。

export class PublishError extends Error {
    /**
     * @param {string} message ユーザーへそのまま表示する日本語メッセージ
     * @param {{status?: number, kind?: 'quota'|'file'|'consent'|'moderation'|'network'|'input'}} [opts]
     */
    constructor(message, { status = 400, kind = 'input' } = {}) {
        super(message);
        this.name = 'PublishError';
        this.status = status;
        this.kind = kind;
    }
}

// ── 法務文書の現行バージョン（§15.2 同意記録の正本。文書改定時にここを更新） ──
export const LEGAL_DOCUMENT_VERSIONS = {
    terms: '0.1-draft',
    publishing: '0.2-draft',   // 2026-07-25: 無料枠の容量上限を 200MB へ改定
    content: '0.1-draft',
    privacy: '0.1-draft',
    report: '0.1-draft',
};
// 公開操作に同意必須の文書（report は手続き文書のため同意対象外）
export const REQUIRED_CONSENT_TYPES = ['terms', 'publishing', 'content', 'privacy'];

// ── ファイル制約（§6.1: 拡張子・Content-Type・パスを検査。Content-Type はサーバが決める） ──
export const CONTENT_TYPES = {
    '.json': 'application/json',
    '.glb': 'model/gltf-binary',
    '.png': 'image/png',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.webp': 'image/webp',
    '.mp3': 'audio/mpeg',
    '.ogg': 'audio/ogg',
    '.wav': 'audio/wav',
    '.woff2': 'font/woff2',
};
// 説明文の上限。ギャラリーのカードと共有リンク（OGP）の要約に使う短い紹介文で、
// 実際に見えるのは先頭100文字程度（カードは2行・OGPは各SNSが途中で切る）。
// 小説投稿サイトの「あらすじ／紹介文」と同じ感覚で書ける長さに合わせている
export const MAX_DESCRIPTION_CHARS = 300;

export const MAX_FILES_PER_GAME = 2000;
export const MAX_SINGLE_FILE_BYTES = 60 * 1024 * 1024; // GLB 単体の上限（クォータとは別の異常値ガード）

const FILE_PATH_PATTERN = /^(maps|cast)\/[A-Za-z0-9_\-][A-Za-z0-9_\-./]*$/;

/** gameId をツール設定（reserved-game-ids.json）で検証する。 */
export function validateGameId(gameId, reservedConfig) {
    if (typeof gameId !== 'string' || gameId === '') {
        throw new PublishError('gameId を指定してください。');
    }
    if (!new RegExp(reservedConfig.pattern).test(gameId) || gameId.length > reservedConfig.maxLength || gameId.length < 3) {
        throw new PublishError(`gameId の形式が不正です（英小文字はじまり・英数字/_/-・3〜${reservedConfig.maxLength}文字）: ${gameId}`);
    }
    const reserved = new Set([...(reservedConfig.reserved ?? []), ...(reservedConfig.reservedLanguageCodes ?? [])]);
    if (reserved.has(gameId)) {
        throw new PublishError(`gameId「${gameId}」は予約語のため使用できません。`);
    }
    return gameId;
}

/**
 * アップロード対象ファイル一覧を検証し、Content-Type を確定して返す（§6.1）。
 * @param {Array<{path: string, size: number}>} files
 * @returns {Array<{path: string, size: number, contentType: string}>}
 */
export function validateUploadFiles(files) {
    if (!Array.isArray(files) || files.length === 0) {
        throw new PublishError('アップロードするファイルがありません。', { kind: 'file' });
    }
    if (files.length > MAX_FILES_PER_GAME) {
        throw new PublishError(`ファイル数が上限（${MAX_FILES_PER_GAME}件）を超えています。`, { kind: 'file' });
    }
    const seen = new Set();
    const out = [];
    for (const f of files) {
        const p = typeof f?.path === 'string' ? f.path : '';
        if (!FILE_PATH_PATTERN.test(p) || p.includes('..') || p.includes('//') || p.includes('./') || p.endsWith('/')) {
            throw new PublishError(`不正なファイルパスです: ${p || '(空)'}`, { kind: 'file' });
        }
        // 二重拡張子（.glb.html 等）と隠しファイルを拒否
        const name = p.slice(p.lastIndexOf('/') + 1);
        if (name.startsWith('.')) throw new PublishError(`不正なファイル名です: ${p}`, { kind: 'file' });
        const ext = name.slice(name.lastIndexOf('.'));
        const contentType = CONTENT_TYPES[ext.toLowerCase()];
        if (!contentType) throw new PublishError(`許可されていない拡張子です: ${p}`, { kind: 'file' });
        if (seen.has(p)) throw new PublishError(`ファイルパスが重複しています: ${p}`, { kind: 'file' });
        seen.add(p);
        const size = Number(f?.size);
        if (!Number.isInteger(size) || size <= 0 || size > MAX_SINGLE_FILE_BYTES) {
            throw new PublishError(`ファイルサイズが不正です（1〜${Math.floor(MAX_SINGLE_FILE_BYTES / 1024 / 1024)}MB）: ${p}`, { kind: 'file' });
        }
        out.push({ path: p, size, contentType });
    }
    return out;
}


/** 説明文を検証して正規化する（空は null）。長すぎる場合は入力エラーとして弾く。 */
export function validateDescription(text) {
    if (text === null || text === undefined) return null;
    const trimmed = String(text).trim();
    if (trimmed === '') return null;
    if (trimmed.length > MAX_DESCRIPTION_CHARS) {
        throw new PublishError(
            `説明は ${MAX_DESCRIPTION_CHARS}文字以内で入力してください（現在 ${trimmed.length}文字）。`,
        );
    }
    return trimmed;
}

/**
 * クォータ免除プランか。管理者（plan='admin'。審査 API の管理者判定 §6 と同じ正）は
 * 容量・件数・操作回数の上限を受けない（本番 E2E 用に運営アカウントが複数本公開するため）。
 * ファイル構造の異常値ガード（validateUploadFiles の件数・単体サイズ・拡張子）は免除しない。
 */
export const isQuotaExempt = (entitlements) => entitlements?.plan === 'admin';

/** クォータ検査（§10）。exceeded 時は kind='quota' の PublishError を投げる。 */
export function assertQuota({ files, entitlements, ownGames, gameId }) {
    const totalBytes = files.reduce((a, f) => a + f.size, 0);
    if (isQuotaExempt(entitlements)) return { totalBytes };   // 管理者は上限なし
    if (totalBytes > entitlements.max_bytes_per_game) {
        throw new PublishError(
            `1ゲームの容量上限（${Math.floor(entitlements.max_bytes_per_game / 1024 / 1024)}MB）を超えています（今回: ${Math.ceil(totalBytes / 1024 / 1024)}MB）。`,
            { kind: 'quota' },
        );
    }
    const isExisting = ownGames.some((g) => g.game_id === gameId);
    if (!isExisting && ownGames.length + 1 > entitlements.max_games) {
        throw new PublishError(`公開できるゲーム数の上限（${entitlements.max_games}件）に達しています。`, { kind: 'quota' });
    }
    const otherBytes = ownGames.filter((g) => g.game_id !== gameId)
        .reduce((a, g) => a + Number(g.bytes_total || 0), 0);
    if (otherBytes + totalBytes > entitlements.max_bytes_total) {
        throw new PublishError(
            `合計容量の上限（${Math.floor(entitlements.max_bytes_total / 1024 / 1024)}MB）を超えています。`,
            { kind: 'quota' },
        );
    }
    return { totalBytes };
}

/** 同意検証（§15.2）。既存記録＋今回送信分で必須文書が現行版で揃うかを確認する。 */
export function assertConsent({ acceptedDocuments, existingAcceptances }) {
    const have = new Set(
        (existingAcceptances ?? [])
            .filter((a) => LEGAL_DOCUMENT_VERSIONS[a.document_type] === a.document_version)
            .map((a) => a.document_type),
    );
    const toRecord = [];
    for (const [type, version] of Object.entries(acceptedDocuments ?? {})) {
        if (LEGAL_DOCUMENT_VERSIONS[type] && LEGAL_DOCUMENT_VERSIONS[type] === version && !have.has(type)) {
            toRecord.push({ document_type: type, document_version: version });
            have.add(type);
        }
    }
    const missing = REQUIRED_CONSENT_TYPES.filter((t) => !have.has(t));
    if (missing.length > 0) {
        throw new PublishError(`公開には規約への同意が必要です（未同意: ${missing.join(', ')}）。`, { status: 428, kind: 'consent' });
    }
    return { toRecord };
}

// 審査中（pending）は作成者側の変更をすべて止める。
// 審査対象のデータと審査結果が食い違う（審査中に rev や公開範囲が変わる・削除される）事故を防ぐ。
export const REVIEW_LOCK_STATUS = 'pending';

/**
 * 審査中なら操作を拒否する。
 * @param {object|null} row published_games の行
 * @param {string} actionLabel 「公開範囲の変更」等、拒否メッセージに入れる操作名
 */
export function assertNotUnderReview(row, actionLabel) {
    if (row?.moderation_status !== REVIEW_LOCK_STATUS) return;
    throw new PublishError(
        `ギャラリー掲載の審査中は${actionLabel}できません。審査の結果が出てから操作してください。`,
        { status: 409, kind: 'moderation' },
    );
}

/**
 * 公開済みゲームの内容変更（更新公開）を拒否する。
 *
 * 一度公開したゲームは「公開を削除 → もう一度公開」だけで差し替える運用に統一する。
 * 更新公開を許すと、審査承認後に中身だけ不適切な内容へ差し替えて一般公開できてしまう
 * （審査の回避経路。2026-07-25 指摘）。削除→再公開なら審査状態も初期化されるため、
 * 内容が変わったゲームが承認済みのまま掲載され続けることがない。
 */
export function assertNotAlreadyPublished(existing) {
    if (!existing || existing.deleted_at) return;
    throw new PublishError(
        '公開済みのゲームは内容を変更できません。変更するには「マイ公開ゲーム」で公開を削除してから、'
        + 'もう一度公開してください（ギャラリー掲載の審査は最初からやり直しになります）。',
        { status: 409, kind: 'moderation' },
    );
}

/**
 * 運営停止中の操作を拒否する。停止は運営対応（通報・規約違反）のためで、
 * 作成者側の変更（公開範囲・更新公開・削除）は保全の妨げになるので通さない（§11.3）。
 */
export function assertNotSuspended(row, actionLabel) {
    if (row?.moderation_status !== 'suspended') return;
    throw new PublishError(
        `このゲームは運営により停止されています。${actionLabel}はできません。通報・審査の結果をご確認ください。`,
        { status: 403, kind: 'moderation' },
    );
}

/**
 * ギャラリー掲載を申請できる状態かを確かめる。
 * 非公開のまま申請しても「掲載されても遊べない」矛盾になるため、公開範囲の条件も見る（§11.1）。
 */
export function assertCanRequestListing(row) {
    if (row?.visibility === 'private') {
        throw new PublishError(
            '非公開のゲームはギャラリー掲載を申請できません。先に「限定公開」へ変更してください。',
            { status: 409, kind: 'moderation' },
        );
    }
    if (!['unreviewed', 'rejected'].includes(row?.moderation_status)) {
        throw new PublishError(
            `現在の審査状態（${row?.moderation_status}）では申請できません。`,
            { status: 409, kind: 'moderation' },
        );
    }
}

/**
 * 論理削除済みの自分のゲームを、同じ gameId で公開し直すときの復帰パッチ（§11.3）。
 * 誤って削除しても手元のデータから再公開できるようにする（日次GCの物理削除を待たせない）。
 * 削除時に private へ落としているため、復帰時は初回公開と同じ unlisted に戻す。
 */
export function buildRepublishPatch(existing) {
    if (!existing?.deleted_at) return {};
    // 削除→再公開は「別の内容」になり得るため、審査状態・特集枠・掲載時刻も初期化する。
    // 承認を引き継ぐと、内容を差し替えたゲームが審査なしで掲載され続けてしまう
    return {
        deleted_at: null,
        visibility: 'unlisted',
        moderation_status: 'unreviewed',
        moderation_note: null,
        featured_rank: null,
        listed_at: null,
    };
}

export const objectKey = (gameId, rev, path) => `games/${gameId}/r${rev}/${path}`;
export const statusKey = (gameId) => `games/${gameId}/status.json`;
export const manifestKey = (gameId, rev) => `games/${gameId}/r${rev}/manifest.json`;

/**
 * 公開時に記録した manifest と、いま R2 にある実体の差分を返す（空配列＝一致）。
 * 目的: 「公開直後の presigned URL（有効期限内）で承認後に実体だけ差し替える」経路を検出する。
 * サイズ一致でも中身が変わっていれば ETag で気づける。etag 未記録（旧 rev）は判定対象外。
 * @param {Array<{path: string, size: number, etag?: string}>} manifestFiles
 * @param {Map<string, {size: number, etag: string|null}>} actual
 */
export function diffPublishedContent(manifestFiles, actual) {
    const changed = [];
    for (const file of manifestFiles ?? []) {
        const found = actual.get(file.path);
        if (!found) { changed.push(`${file.path}: 実体がありません`); continue; }
        if (Number(found.size) !== Number(file.size)) { changed.push(`${file.path}: サイズが変わっています`); continue; }
        if (file.etag && found.etag && file.etag !== found.etag) changed.push(`${file.path}: 内容が変わっています`);
    }
    return changed;
}

export function buildManifest({ gameId, rev, files, totalBytes, title, description = null, now = new Date() }) {
    return {
        formatVersion: '1.0',
        gameId,
        rev,
        title,
        description,
        totalBytes,
        fileCount: files.length,
        // etag は公開確定時の実体の指紋。承認時に再照合して「審査後の差し替え」を検出する
        files: files.map((f) => ({ path: f.path, size: f.size, contentType: f.contentType, etag: f.etag ?? null })),
        createdAt: now.toISOString(),
    };
}

export function buildStatus(row, { now = new Date() } = {}) {
    return {
        formatVersion: '1.0',
        gameId: row.game_id,
        visibility: row.deleted_at ? 'private' : row.visibility,
        moderationStatus: row.moderation_status,
        currentRev: row.current_rev,
        deleted: !!row.deleted_at,
        updatedAt: now.toISOString(),
    };
}

/** JST（UTC+9）の当日 0 時を ISO で返す（max_publish_ops_per_day の日界。JST 0時リセット運用に合わせる） */
export function jstDayStartIso(now = new Date()) {
    const jst = new Date(now.getTime() + 9 * 3600 * 1000);
    const start = Date.UTC(jst.getUTCFullYear(), jst.getUTCMonth(), jst.getUTCDate()) - 9 * 3600 * 1000;
    return new Date(start).toISOString();
}
